import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../../utils/formatters';

const Icon = ({ d, size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    map: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z', 'M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    arrowLeft: ['M19 12H5', 'M12 19l-7-7 7-7'],
    clock: ['M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z', 'M12 6V12L16 14'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8z'],
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z', 'M18.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z'],
};

export default function TripTrackingPage({ tripId, onBack }) {
    const [trip, setTrip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stops, setStops] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [expandedStop, setExpandedStop] = useState(null); // 'origin', 'destination', or stopId
    const [resolvedCoords, setResolvedCoords] = useState(null);
    const [isRouteOverlayOpen, setIsRouteOverlayOpen] = useState(false);
    const mapRef = useRef(null);
    const leafletMap = useRef(null);
    const markers = useRef([]);
    const busMarkerRef = useRef(null);
    const routeLayer = useRef(null);

    const latestLocationRef = useRef(null);

    // Real-time live vehicle GPS polling
    useEffect(() => {
        if (!tripId) return;

        let isMounted = true;

        const updateBusPosition = (lat, lng, locData) => {
            latestLocationRef.current = { lat, lng, locData };
            if (!leafletMap.current) return;

            const newPos = [lat, lng];
            const timeStr = locData?.updatedAt ? new Date(locData.updatedAt).toLocaleTimeString() : new Date().toLocaleTimeString();

            if (busMarkerRef.current) {
                busMarkerRef.current.setLatLng(newPos);
                busMarkerRef.current.bindPopup(`<b>Current Vehicle Location</b><br/>Updated: ${timeStr}`);
            } else {
                const busIconHtml = `
                    <div style="
                        background: white;
                        color: #3b82f6;
                        width: 32px;
                        height: 32px;
                        border-radius: 50%;
                        border: 2px solid #3b82f6;
                        box-shadow: 0 4px 6px rgba(0,0,0,0.3);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    ">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="2" y="4" width="20" height="16" rx="2.5" ry="2.5"></rect>
                            <path d="M6 8h12"></path>
                            <path d="M8 19v2"></path>
                            <path d="M16 19v2"></path>
                            <circle cx="7.5" cy="14.5" r="1.5"></circle>
                            <circle cx="16.5" cy="14.5" r="1.5"></circle>
                        </svg>
                    </div>
                `;
                const busMarker = window.L.marker(newPos, {
                    icon: window.L.divIcon({
                        html: busIconHtml,
                        className: 'custom-bus-marker',
                        iconSize: [32, 32],
                        iconAnchor: [16, 16]
                    }),
                    zIndexOffset: 1000
                }).addTo(leafletMap.current).bindPopup(`<b>Current Vehicle Location</b><br/>Updated: ${timeStr}`);
                busMarkerRef.current = busMarker;
                markers.current.push(busMarker);
            }
        };

        const pollLocation = async () => {
            try {
                // Try driver-location endpoint first
                const res = await fetch(`/api/driver-location/get-location/${tripId}`);
                if (res.ok) {
                    const data = await res.json();
                    if (data && data.success && data.location && isMounted) {
                        const { latitude, longitude } = data.location;
                        if (latitude != null && longitude != null) {
                            updateBusPosition(Number(latitude), Number(longitude), data.location);
                            return;
                        }
                    }
                }

                // Fallback 1: passenger_api live-driver-location
                const res2 = await fetch(`/api/passenger/live-driver-location`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ tripId })
                });
                if (res2.ok) {
                    const data2 = await res2.json();
                    if (data2 && data2.success && data2.location && isMounted) {
                        const { latitude, longitude } = data2.location;
                        if (latitude != null && longitude != null) {
                            updateBusPosition(Number(latitude), Number(longitude), data2.location);
                            return;
                        }
                    }
                }

                // Fallback 2: driver-location live-driver-location
                const res3 = await fetch(`/api/driver-location/live-driver-location/${tripId}`);
                if (res3.ok) {
                    const data3 = await res3.json();
                    if (data3 && data3.success && data3.location && isMounted) {
                        const { latitude, longitude } = data3.location;
                        if (latitude != null && longitude != null) {
                            updateBusPosition(Number(latitude), Number(longitude), data3.location);
                        }
                    }
                }
            } catch (err) {
                // Ignore transient network errors
            }
        };

        // Poll immediately and every 5 seconds
        pollLocation();
        const interval = setInterval(pollLocation, 5000);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [tripId]);

    // Default coordinates (Bengaluru)
    const DEFAULT_LAT = 12.9716;
    const DEFAULT_LNG = 77.5946;

    const togglePassengerStatus = (allocId, currentStatus) => {
        const newStatus = currentStatus === 'Boarded' ? 'Pending' : 'Boarded';
        fetch(`/api/passenger-allocations/${allocId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: newStatus })
        })
        .then(res => res.json())
        .then(updated => {
            if (updated && !updated.message) {
                setAllocations(prev => prev.map(a => a._id === allocId ? { ...a, status: newStatus } : a));
            }
        })
        .catch(err => console.error("Error updating passenger status:", err));
    };

    useEffect(() => {
        if (!tripId) return;
        fetch(`/api/passenger-allocations/trip/${tripId}`)
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setAllocations(data);
                } else {
                    setAllocations([]);
                }
            })
            .catch(err => {
                console.error("Error loading allocations:", err);
                setAllocations([]);
            });
    }, [tripId]);

    useEffect(() => {
        if (!tripId) return;
        fetch(`/api/trips/single/${tripId}`)
            .then(r => r.json())
            .then(data => {
                if (data && !data.message) {
                    setTrip(data);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error("Error loading trip for tracking:", err);
                setLoading(false);
            });
    }, [tripId]);

    useEffect(() => {
        if (!window.L || !mapRef.current) return;

        // Ensure we don't initialize the map multiple times
        if (leafletMap.current) return;

        // Initialize Leaflet Map
        const map = window.L.map(mapRef.current, {
            zoomControl: false,
            attributionControl: false
        }).setView([DEFAULT_LAT, DEFAULT_LNG], 13);

        leafletMap.current = map;

        // Use OpenStreetMap standard tiles
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map);

        window.L.control.zoom({ position: 'bottomright' }).addTo(map);

        // Cleanup on unmount
        return () => {
            if (leafletMap.current) {
                leafletMap.current.remove();
                leafletMap.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!trip) return;
        const routeId = trip.routeId?._id || trip.routeId;
        if (routeId) {
            fetch(`/api/route-stops/${routeId}`)
                .then(res => res.json())
                .then(data => {
                    if (Array.isArray(data)) {
                        setStops(data);
                    } else {
                        setStops([]);
                    }
                })
                .catch(err => {
                    console.error("Error fetching route stops:", err);
                    setStops([]);
                });
        } else {
            setStops([]);
        }
    }, [trip]);

    useEffect(() => {
        if (!trip) return;
        
        const fetchCoords = async () => {
            let pLat = trip.routeId?.pickupLat || trip.pickupLat;
            let pLng = trip.routeId?.pickupLng || trip.pickupLng;
            let dLat = trip.routeId?.dropLat || trip.dropLat;
            let dLng = trip.routeId?.dropLng || trip.dropLng;
            
            const pLoc = trip.routeId?.pickupLocation || trip.pickupLocation;
            const dLoc = trip.routeId?.dropLocation || trip.dropLocation;

            const geocode = async (locStr) => {
                try {
                    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locStr)}&limit=1&accept-language=en`);
                    const data = await res.json();
                    if (data && data.length > 0) {
                        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
                    }
                } catch (e) {
                    console.error("Geocoding failed for", locStr);
                }
                return null;
            };

            // If coordinates are missing, try to geocode the location strings
            if ((!pLat || !pLng) && pLoc) {
                const coords = await geocode(pLoc);
                if (coords) { pLat = coords.lat; pLng = coords.lng; }
            }
            if ((!dLat || !dLng) && dLoc) {
                const coords = await geocode(dLoc);
                if (coords) { dLat = coords.lat; dLng = coords.lng; }
            }

            setResolvedCoords({
                pickupLat: pLat || DEFAULT_LAT,
                pickupLng: pLng || DEFAULT_LNG,
                dropLat: dLat,
                dropLng: dLng
            });
        };

        fetchCoords();
    }, [trip]);

    useEffect(() => {
        if (!leafletMap.current || !trip || !resolvedCoords) return;

        const { pickupLat, pickupLng, dropLat, dropLng } = resolvedCoords;

        // Clear existing markers and route
        markers.current.forEach(m => m.remove());
        markers.current = [];
        if (routeLayer.current && leafletMap.current) {
            leafletMap.current.removeLayer(routeLayer.current);
            routeLayer.current = null;
        }

        const pickupPos = [pickupLat, pickupLng];
        leafletMap.current.setView(pickupPos, 14);

        // Add Pickup Marker
        const pMarker = window.L.circleMarker(pickupPos, {
            radius: 8,
            fillColor: "#10b981",
            color: "#fff",
            weight: 2,
            opacity: 1,
            fillOpacity: 1
        }).addTo(leafletMap.current).bindPopup('Pickup Location');
        markers.current.push(pMarker);

        // Add Vehicle (Bus) Marker at the starting position
        const busIconHtml = `
            <div style="
                background: white;
                color: #3b82f6;
                width: 32px;
                height: 32px;
                border-radius: 50%;
                border: 2px solid #3b82f6;
                box-shadow: 0 4px 6px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
            ">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2.5" ry="2.5"></rect>
                    <path d="M6 8h12"></path>
                    <path d="M8 19v2"></path>
                    <path d="M16 19v2"></path>
                    <circle cx="7.5" cy="14.5" r="1.5"></circle>
                    <circle cx="16.5" cy="14.5" r="1.5"></circle>
                </svg>
            </div>
        `;

        const initialBusPos = latestLocationRef.current 
            ? [latestLocationRef.current.lat, latestLocationRef.current.lng] 
            : pickupPos;
        const initialTimeStr = latestLocationRef.current?.locData?.updatedAt 
            ? new Date(latestLocationRef.current.locData.updatedAt).toLocaleTimeString() 
            : new Date().toLocaleTimeString();

        const busMarker = window.L.marker(initialBusPos, {
            icon: window.L.divIcon({
                html: busIconHtml,
                className: 'custom-bus-marker',
                iconSize: [32, 32],
                iconAnchor: [16, 16]
            }),
            zIndexOffset: 1000
        }).addTo(leafletMap.current).bindPopup(`<b>Current Vehicle Location</b><br/>Updated: ${initialTimeStr}`);
        busMarkerRef.current = busMarker;
        markers.current.push(busMarker);

        // Add Stops Markers
        stops.forEach((stop, index) => {
            const stopPos = [stop.lat, stop.lng];
            const stopMarker = window.L.marker(stopPos, {
                icon: window.L.divIcon({
                    html: `<div style="
                        background: #3b82f6;
                        color: white;
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        border: 2px solid white;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 11px;
                        font-weight: 800;
                    ">${index + 1}</div>`,
                    className: 'custom-stop-marker',
                    iconSize: [24, 24],
                    iconAnchor: [12, 12]
                })
            }).addTo(leafletMap.current).bindPopup(`Stop ${index + 1}: ${stop.name}`);
            markers.current.push(stopMarker);
        });

        // Add Drop Marker if coordinates exist
        if (dropLat && dropLng) {
            const dropPos = [dropLat, dropLng];
            const dMarker = window.L.circleMarker(dropPos, {
                radius: 8,
                fillColor: "#ef4444",
                color: "#fff",
                weight: 2,
                opacity: 1,
                fillOpacity: 1
            }).addTo(leafletMap.current).bindPopup('Drop Location');
            markers.current.push(dMarker);
        }

        // Fetch Route from OSRM passing through available points
        const coordinates = [];
        coordinates.push(`${pickupLng},${pickupLat}`); // Start is always present (with defaults)
        
        stops.forEach(s => {
            if (s.lng && s.lat) {
                coordinates.push(`${s.lng},${s.lat}`);
            }
        });
        
        if (dropLat && dropLng) {
            coordinates.push(`${dropLng},${dropLat}`);
        }

        if (coordinates.length >= 2) {
            const coordsString = coordinates.join(';');

            fetch(`https://router.project-osrm.org/route/v1/driving/${coordsString}?overview=full&geometries=geojson`)
                .then(res => res.json())
                .then(data => {
                    if (data.routes && data.routes.length > 0) {
                        const routeCoords = data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
                        
                        if (routeLayer.current && leafletMap.current) {
                            leafletMap.current.removeLayer(routeLayer.current);
                        }

                        routeLayer.current = window.L.polyline(routeCoords, {
                            color: '#3b82f6', // Blue route line
                            weight: 5,
                            opacity: 0.8,
                            lineJoin: 'round'
                        }).addTo(leafletMap.current);

                        leafletMap.current.fitBounds(routeLayer.current.getBounds(), { padding: [30, 30] });
                    }
                })
                .catch(err => console.error("Error fetching route:", err));
        }

        // Fit bounds to show all markers initially
        if (markers.current.length > 0) {
            const group = new window.L.featureGroup(markers.current);
            leafletMap.current.fitBounds(group.getBounds().pad(0.2));
        }
    }, [trip, stops, resolvedCoords]);

    const displayTrip = trip || { 
        _id: tripId, 
        pickupLocation: 'Loading location...', 
        dropLocation: 'Loading destination...',
        driverId: { name: 'Assigned Driver' },
        vehicleId: { registrationNumber: 'Vehicle' }
    };

    return (
        <div style={{ animation: 'fadeUp 0.4s ease-out' }}>
            {/* Header / Nav */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, background: 'white', padding: '16px 24px', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button 
                        onClick={onBack}
                        style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}
                        className="back-btn-hover"
                    >
                        <Icon d={icons.arrowLeft} size={18} color="#475569" />
                    </button>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', animation: 'pulse 2s infinite' }}></div>
                            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Live Trip Tracking</h2>
                        </div>
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Trip ID: {displayTrip._id?.slice(-8).toUpperCase()}</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ background: '#eff6ff', padding: '8px 16px', borderRadius: 12, border: '1px solid #dbeafe', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Icon d={icons.clock} size={14} color="#3b82f6" />
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>ETA: 12 mins</span>
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
                {/* Map Area */}
                <div style={{ background: 'white', borderRadius: 24, border: '1px solid #e2e8f0', overflow: 'hidden', height: 'calc(100vh - 220px)', position: 'relative', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)' }}>
                    <div ref={mapRef} style={{ width: '100%', height: '100%', zIndex: 1 }}></div>
                    
                    {/* Floating Info Overlay */}
                    <div style={{ position: 'absolute', top: 20, left: 20, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', padding: isRouteOverlayOpen ? '16px' : '12px 16px', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', width: isRouteOverlayOpen ? 280 : 'auto', pointerEvents: 'auto', zIndex: 1000, maxHeight: '80%', overflowY: 'auto', transition: 'all 0.3s ease' }}>
                        <div 
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 16 }}
                            onClick={() => setIsRouteOverlayOpen(!isRouteOverlayOpen)}
                        >
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Icon d={icons.map} size={14} color="#94a3b8" />
                                Current Route
                            </div>
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b' }}>
                                {isRouteOverlayOpen ? '▼' : '▶'}
                            </div>
                        </div>
                        
                        {isRouteOverlayOpen && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                                {/* Pickup point */}
                            <div style={{ display: 'flex', gap: 10 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981', border: '2px solid white', boxShadow: '0 0 0 2px #10b981' }}></div>
                                    <div style={{ width: 2, flex: 1, minHeight: '15px', background: '#e2e8f0' }}></div>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', flex: 1 }}>
                                    {displayTrip.routeId?.pickupLocation || displayTrip.pickupLocation || 'Start Location'}
                                    <div style={{ fontSize: 11, fontWeight: 500, color: '#64748b', marginTop: 2 }}>Departed at {displayTrip.startTime || '09:00 AM'}</div>
                                    
                                    {/* Pickup Passengers */}
                                    {(() => {
                                        const pickups = allocations.filter(a => !a.pickupStopId);
                                        if (pickups.length === 0) return null;
                                        const isExpanded = expandedStop === 'origin';
                                        return (
                                            <div style={{ marginTop: 4 }}>
                                                <button
                                                    onClick={() => setExpandedStop(isExpanded ? null : 'origin')}
                                                    style={{
                                                        background: '#ecfdf5',
                                                        border: '1px solid #a7f3d0',
                                                        padding: '2px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        color: '#059669',
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 4,
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    ↑ {pickups.length} Boarding {isExpanded ? '▲' : '▼'}
                                                </button>
                                                
                                                {isExpanded && (
                                                    <div style={{
                                                        marginTop: 6,
                                                        padding: '8px 10px',
                                                        background: '#f8fafc',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 6
                                                    }}>
                                                        {pickups.map((a, i) => (
                                                            <div key={i} style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, width: '100%' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                    <span style={{ color: '#059669', fontWeight: 800 }}>↑</span> {a.passengerId?.name || 'Passenger'}
                                                                </div>
                                                                <span 
                                                                    onClick={() => togglePassengerStatus(a._id, a.status)}
                                                                    style={{
                                                                        fontSize: '9px',
                                                                        fontWeight: 800,
                                                                        padding: '2px 6px',
                                                                        borderRadius: '4px',
                                                                        background: a.status === 'Boarded' ? '#d1fae5' : '#f3f4f6',
                                                                        color: a.status === 'Boarded' ? '#065f46' : '#4b5563',
                                                                        cursor: 'pointer',
                                                                        userSelect: 'none',
                                                                        border: '1px solid',
                                                                        borderColor: a.status === 'Boarded' ? '#a7f3d0' : '#e5e7eb',
                                                                        transition: 'all 0.15s'
                                                                    }}
                                                                >
                                                                    {a.status || 'Pending'}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Intermediate stops */}
                            {stops.map((stop, sIdx) => (
                                <div key={stop._id || sIdx} style={{ display: 'flex', gap: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                        <div style={{ 
                                            width: 16, 
                                            height: 16, 
                                            borderRadius: '50%', 
                                            background: '#3b82f6', 
                                            color: 'white', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            fontSize: '9px',
                                            fontWeight: 800
                                        }}>{sIdx + 1}</div>
                                        <div style={{ width: 2, flex: 1, minHeight: '15px', background: '#e2e8f0' }}></div>
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', flex: 1 }}>
                                        {stop.name}
                                        <div style={{ fontSize: 10, fontWeight: 500, color: '#64748b', marginTop: 1 }}>Stop {sIdx + 1}</div>
                                        
                                        {/* Stop Pickups & Drops */}
                                        {(() => {
                                            const pCount = allocations.filter(a => (a.pickupStopId?._id || a.pickupStopId) === stop._id);
                                            const dCount = allocations.filter(a => (a.dropStopId?._id || a.dropStopId) === stop._id);
                                            if (pCount.length === 0 && dCount.length === 0) return null;
                                            const isExpanded = expandedStop === stop._id;
                                            return (
                                                <div style={{ marginTop: 4 }}>
                                                    <button
                                                        onClick={() => setExpandedStop(isExpanded ? null : stop._id)}
                                                        style={{
                                                            background: '#eff6ff',
                                                            border: '1px solid #bfdbfe',
                                                            padding: '2px 8px',
                                                            borderRadius: '6px',
                                                            fontSize: '10px',
                                                            fontWeight: 700,
                                                            color: '#1d4ed8',
                                                            cursor: 'pointer',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 4,
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        {pCount.length > 0 && `↑ ${pCount.length} Boarding`}
                                                        {pCount.length > 0 && dCount.length > 0 && ` | `}
                                                        {dCount.length > 0 && `↓ ${dCount.length} Deboarding`}
                                                        {` `} {isExpanded ? '▲' : '▼'}
                                                    </button>
                                                    
                                                    {isExpanded && (
                                                        <div style={{
                                                            marginTop: 6,
                                                            padding: '8px 10px',
                                                            background: '#f8fafc',
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '8px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 6
                                                        }}>
                                                            {pCount.map((a, i) => (
                                                                <div key={`p-${i}`} style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, width: '100%' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                        <span style={{ color: '#059669', fontWeight: 800 }}>↑</span> {a.passengerId?.name || 'Passenger'}
                                                                    </div>
                                                                    <span 
                                                                        onClick={() => togglePassengerStatus(a._id, a.status)}
                                                                        style={{
                                                                            fontSize: '9px',
                                                                            fontWeight: 800,
                                                                            padding: '2px 6px',
                                                                            borderRadius: '4px',
                                                                            background: a.status === 'Boarded' ? '#d1fae5' : '#f3f4f6',
                                                                            color: a.status === 'Boarded' ? '#065f46' : '#4b5563',
                                                                            cursor: 'pointer',
                                                                            userSelect: 'none',
                                                                            border: '1px solid',
                                                                            borderColor: a.status === 'Boarded' ? '#a7f3d0' : '#e5e7eb',
                                                                            transition: 'all 0.15s'
                                                                        }}
                                                                    >
                                                                        {a.status || 'Pending'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                            {dCount.map((a, i) => (
                                                                <div key={`d-${i}`} style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, width: '100%' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                        <span style={{ color: '#ef4444', fontWeight: 800 }}>↓</span> {a.passengerId?.name || 'Passenger'}
                                                                    </div>
                                                                    <span 
                                                                        onClick={() => togglePassengerStatus(a._id, a.status)}
                                                                        style={{
                                                                            fontSize: '9px',
                                                                            fontWeight: 800,
                                                                            padding: '2px 6px',
                                                                            borderRadius: '4px',
                                                                            background: a.status === 'Boarded' ? '#d1fae5' : '#f3f4f6',
                                                                            color: a.status === 'Boarded' ? '#065f46' : '#4b5563',
                                                                            cursor: 'pointer',
                                                                            userSelect: 'none',
                                                                            border: '1px solid',
                                                                            borderColor: a.status === 'Boarded' ? '#a7f3d0' : '#e5e7eb',
                                                                            transition: 'all 0.15s'
                                                                        }}
                                                                    >
                                                                        {a.status || 'Pending'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            ))}

                            {/* Drop point */}
                            <div style={{ display: 'flex', gap: 10 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', border: '2px solid white', boxShadow: '0 0 0 2px #ef4444' }}></div>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', flex: 1 }}>
                                    {displayTrip.routeId?.dropLocation || displayTrip.dropLocation || 'End Location'}
                                    <div style={{ fontSize: 11, fontWeight: 500, color: '#64748b', marginTop: 2 }}>Estimated arrival 09:45 AM</div>
                                    
                                    {/* Drop Passengers */}
                                    {(() => {
                                        const drops = allocations.filter(a => !a.dropStopId);
                                        if (drops.length === 0) return null;
                                        const isExpanded = expandedStop === 'destination';
                                        return (
                                            <div style={{ marginTop: 4 }}>
                                                <button
                                                    onClick={() => setExpandedStop(isExpanded ? null : 'destination')}
                                                    style={{
                                                        background: '#fef2f2',
                                                        border: '1px solid #fca5a5',
                                                        padding: '2px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        color: '#ef4444',
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 4,
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    ↓ {drops.length} Deboarding {isExpanded ? '▲' : '▼'}
                                                </button>
                                                
                                                {isExpanded && (
                                                    <div style={{
                                                        marginTop: 6,
                                                        padding: '8px 10px',
                                                        background: '#f8fafc',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 6
                                                    }}>
                                                        {drops.map((a, i) => (
                                                            <div key={i} style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, width: '100%' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                    <span style={{ color: '#ef4444', fontWeight: 800 }}>↓</span> {a.passengerId?.name || 'Passenger'}
                                                                </div>
                                                                <span 
                                                                    onClick={() => togglePassengerStatus(a._id, a.status)}
                                                                    style={{
                                                                        fontSize: '9px',
                                                                        fontWeight: 800,
                                                                        padding: '2px 6px',
                                                                        borderRadius: '4px',
                                                                        background: a.status === 'Boarded' ? '#d1fae5' : '#f3f4f6',
                                                                        color: a.status === 'Boarded' ? '#065f46' : '#4b5563',
                                                                        cursor: 'pointer',
                                                                        userSelect: 'none',
                                                                        border: '1px solid',
                                                                        borderColor: a.status === 'Boarded' ? '#a7f3d0' : '#e5e7eb',
                                                                        transition: 'all 0.15s'
                                                                    }}
                                                                >
                                                                    {a.status || 'Pending'}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                </div>

                {/* Info Sidebar */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Driver Card */}
                    <div style={{ background: 'white', borderRadius: 24, border: '1px solid #e2e8f0', padding: 24, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 16 }}>Assigned Driver</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                            <div style={{ width: 56, height: 56, borderRadius: 16, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
                                <Icon d={icons.user} size={28} color="#64748b" />
                            </div>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{displayTrip.driverId?.name || 'Driver Name'}</div>
                                <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>{displayTrip.driverId?.phoneNumber || '+91 98XXX XXXXX'}</div>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <button style={{ padding: '10px', borderRadius: 12, border: '1px solid #e2e8f0', background: 'white', fontSize: 12, fontWeight: 700, color: '#334155', cursor: 'pointer' }}>Call Driver</button>
                            <button style={{ padding: '10px', borderRadius: 12, border: 'none', background: '#3b82f6', fontSize: 12, fontWeight: 700, color: 'white', cursor: 'pointer' }}>Message</button>
                        </div>
                    </div>

                    {/* Vehicle Card */}
                    <div style={{ background: 'white', borderRadius: 24, border: '1px solid #e2e8f0', padding: 24, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 16 }}>Vehicle Info</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <div style={{ width: 48, height: 48, borderRadius: 14, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #dbeafe' }}>
                                <Icon d={icons.truck} size={24} color="#3b82f6" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
                                        {displayTrip.vehicleId?.plateNumber || displayTrip.vehicleId?.registrationNumber || displayTrip.vehicleCode || 'KA-01-XXXX'}
                                    </div>
                                    {(displayTrip.vehicleId?.vehicleCode || displayTrip.vehicleCode) && (
                                        <span style={{ fontSize: 11, fontWeight: 800, background: '#ecfdf5', color: '#059669', padding: '2px 8px', borderRadius: 6, border: '1px solid #a7f3d0' }}>
                                            Code: {displayTrip.vehicleId?.vehicleCode || displayTrip.vehicleCode}
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginTop: 4 }}>
                                    {[displayTrip.vehicleId?.make, displayTrip.vehicleId?.model, displayTrip.vehicleId?.vehicleType].filter(Boolean).join(' • ') || 'Fleet Vehicle'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                .back-btn-hover:hover { background: #f1f5f9 !important; border-color: #cbd5e1 !important; transform: translateX(-2px); }
                @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes pulse { 0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); } 70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); } 100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); } }
                .leaflet-container { font-family: inherit; }
            `}</style>
        </div>
    );
}


